export type SolutionPlatform = 'SKP' | 'Human Capability' | 'Both'

export const solutionScenarios = [
  {
    setting: 'A research institution',
    need: 'may need to discover relationships across thousands of papers.',
  },
  {
    setting: 'A school',
    need: 'may need better pathways for developing reasoning.',
  },
  {
    setting: 'A public institution',
    need: 'may need to connect policy, staff development, and reviewable decisions.',
  },
]

export const solutionSettings = [
  {
    id: 'scientific-research',
    setting: 'For scientific research',
    tagline: 'Connect ideas across disciplines and documents.',
    platform: 'SKP' as SolutionPlatform,
    link: '/semantic-knowledge',
    linkLabel: 'Explore SKP',
    paragraphs: [
      'SKP can help research organizations organize papers, people, concepts, methods, populations, and findings in a knowledge graph. AI proposes entities and relationships from source material; approved vocabularies, validation rules, and human reviewers govern what becomes part of the trusted knowledge base.',
      'Researchers could search by concept and relationship, follow a finding to its source passage, compare related studies, and discover connections that keyword searches may miss. Semantic search and GraphRAG make that knowledge easier to explore while keeping its origins visible.',
    ],
    applications: [
      'Literature discovery',
      'Biomedical knowledge graphs',
      'Research portals',
      'Evidence mapping',
      'Cross-disciplinary collaboration',
    ],
  },
  {
    id: 'education',
    setting: 'For education',
    tagline: 'Build reasoning and self-direction over time.',
    platform: 'Both' as SolutionPlatform,
    link: '/education',
    linkLabel: 'Education in depth',
    paragraphs: [
      'CJI and NCJI research can inform learning activities focused on evaluating claims, considering consequences, planning, cooperation, and resilience to manipulation. SKP can connect each activity to its capability definition, instructional material, and research basis.',
      'At early stages, the emphasis is on support and growth. As learners progress, programs can examine which activities help and revise them accordingly.',
    ],
    applications: [
      'Reasoning curricula',
      'Media literacy',
      'Mentoring pathways',
      'Longitudinal development pilots',
    ],
  },
  {
    id: 'employers',
    setting: 'For employers and workforce programs',
    tagline: 'Connect strengths to development and opportunity.',
    platform: 'Both' as SolutionPlatform,
    link: '/employers',
    linkLabel: 'Employers in depth',
    paragraphs: [
      'Organizations can use the Human Capability Platform to explore decision quality, planning, communication, and leadership development. Findings can lead to practice, coaching, or learning opportunities, with follow-up to see whether they helped.',
      'SKP can maintain a consistent language for skills and role requirements across teams and programs. Opportunity matching can explain why a path may fit a person’s demonstrated strengths while keeping guidance separate from a consequential eligibility decision.',
    ],
    applications: [
      'Workforce development',
      'Internal mobility',
      'Mentoring',
      'Leadership programs',
    ],
  },
  {
    id: 'civil-society',
    setting: 'For civil society',
    tagline: 'Strengthen local knowledge and constructive participation.',
    platform: 'Both' as SolutionPlatform,
    link: '/civil-society',
    linkLabel: 'Civil society in depth',
    paragraphs: [
      'Nonprofits, community organizations, and mentorship networks often hold valuable knowledge in disconnected reports and programs. SKP can connect that knowledge and make it easier to find what has been tried, what was learned, and where support is needed.',
      'Human capability programs can focus on media literacy, independent judgment, cooperation, and practical pathways to participation.',
    ],
    applications: [
      'Community knowledge hubs',
      'Misinformation resilience education',
      'Local mentoring',
      'Opportunity mapping',
    ],
  },
  {
    id: 'government',
    setting: 'For government and public institutions',
    tagline: 'Make institutional knowledge usable and decisions reviewable.',
    platform: 'Both' as SolutionPlatform,
    link: '/government',
    linkLabel: 'Government in depth',
    paragraphs: [
      'SKP can organize policies, program knowledge, role requirements, and approved rules so staff can find current guidance and understand how ideas and decisions connect. The Human Capability Platform can support research and pilots in public-sector development and leadership readiness.',
    ],
    safeguard:
      'Where a proposed application could affect a person’s access or standing, additional systems are required: validated measures, secure identity and case management, authorized human decisions, notice, independent review, appeals, and correction. SKP supplies the knowledge foundation; it does not replace those operational and governance systems.',
    applications: [
      'Policy knowledge systems',
      'Civil-service learning',
      'Program planning',
      'Carefully evaluated civic development pilots',
    ],
  },
  {
    id: 'leadership',
    setting: 'For leadership development',
    tagline: 'Prepare people for decisions with wider consequences.',
    platform: 'Both' as SolutionPlatform,
    link: '/leadership',
    linkLabel: 'Leadership in depth',
    paragraphs: [
      'Leadership requires reasoning under pressure, attention to long-term effects, the ability to hear dissent, and responsibility for institutional outcomes. SyNeural’s research explores how these capabilities can be developed and assessed for specific contexts.',
    ],
    safeguard:
      'SKP connects leadership frameworks to their definitions, research, tasks, and development activities. Any proposed qualification use would require validation for the role, an explanation of findings, and a meaningful route to review and reassessment.',
    applications: [
      'Executive education',
      'Crisis exercises',
      'Succession development',
      'Institutional leadership research',
    ],
  },
]

export const solutionCycle = [
  'Connect knowledge',
  'Define the problem',
  'Develop capability',
  'Support action',
  'Examine outcomes',
  'Improve',
]
